import React, { type PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { UL } from '@expo/html-elements';

import { useA11ySelector } from '@sg/a11y-helpers';
import { ShadowDivider, theme, useContainerSpacing, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsCategoriesNavList = (props: PropsWithChildren) => {
  const { children } = props;

  const { currentBreakpoint, match } = useResponsive();
  const containerSpacing = useContainerSpacing();
  const { right: safeAreaRightInset } = useSafeAreaInsets();
  const a11ySelector = useA11ySelector('gift-cards.filter');
  const gradientOpacitySV = useSharedValue(1);

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const hasReachedEndRef = useRef(false);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyRightInset = currentBreakpoint.isXS && safeAreaRightInset > 0;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const updateGradientOpacity = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;

      const remainingOffset = contentSize.width - layoutMeasurement.width - contentOffset.x;
      const hasReachedEnd = remainingOffset <= SCROLL_THRESHOLD;

      if (hasReachedEnd === hasReachedEndRef.current) {
        return;
      }

      hasReachedEndRef.current = hasReachedEnd;
      gradientOpacitySV.set(withTiming(hasReachedEnd ? 0 : 1, GRADIENT_TIMING));
    },
    [gradientOpacitySV],
  );

  // ─── Styles ────────────────────────────────────────────────────────────────

  const navContainerDynamicStyles = useMemo<ViewStyle>(
    () => ({ paddingHorizontal: containerSpacing }),
    [containerSpacing],
  );
  const navContainerStyles = [styles.navContainer, navContainerDynamicStyles];
  const navListStyles = [styles.navList, match([styles.navListXS, styles.navListSM])];

  const scrollContainerDynamicStyles = useStyle(
    () => ({ marginRight: shouldApplyRightInset ? safeAreaRightInset : 0 }),
    [shouldApplyRightInset, safeAreaRightInset],
  );

  const gradientAnimatedStyles = useAnimatedStyle(() => ({
    opacity: gradientOpacitySV.value,
  }));
  const gradientStyles = [styles.gradient, gradientAnimatedStyles];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.innerContainer}>
      <View style={scrollContainerDynamicStyles}>
        <ScrollView
          contentContainerStyle={navContainerStyles}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={updateGradientOpacity}
        >
          <UL style={navListStyles} {...a11ySelector.props}>
            {children}
          </UL>
        </ScrollView>

        {shouldApplyRightInset ? (
          <Animated.View pointerEvents="none" style={gradientStyles}>
            <LinearGradient
              colors={GRADIENT_COLORS}
              end={GRADIENT_END}
              start={GRADIENT_START}
              style={styles.gradientFill}
            />
          </Animated.View>
        ) : null}
      </View>

      <View style={styles.shadowContainer}>
        <ShadowDivider height={theme.spacing['2']} color={theme.colors.APP_BACKGROUND} />
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_COLORS = [
  theme.colors.OPACITY.CREAM.TRANSPARENT,
  theme.colors.APP_BACKGROUND,
] as const;
const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 1, y: 0 } as const;
const GRADIENT_TIMING = { duration: theme.transitions.base } as const;

const SCROLL_THRESHOLD = 4;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  innerContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  gradient: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: theme.spacing['12'],
  },
  gradientFill: {
    flex: 1,
  },
  navContainer: {
    minWidth: '100%',
    alignItems: 'center',
  },
  navList: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    columnGap: theme.spacing['4'],
    paddingVertical: theme.spacing['4'],
  },
  navListXS: {
    columnGap: theme.spacing['2'],
  },
  navListSM: {
    justifyContent: 'center',
    columnGap: theme.spacing['4'],
  },
  shadowContainer: {
    position: 'absolute',
    right: 0,
    bottom: -theme.spacing['2'],
    left: 0,
  },
});
